import Text from '@shared/components/content/text';
import Unit, { UnitVariant } from '@shared/components/content/unit';
import { cn, formatNumber } from '@shared/lib/utils';
import { ClassValue } from 'clsx';
import { ReactNode } from 'react';

type MetricSize = 'lg' | 'md' | 'sm';

interface VariantStyle {
  titleClassName: ClassValue;
  valueClassName?: ClassValue;
  unitClassName?: ClassValue;
}

const sizeStyles: Record<MetricSize, VariantStyle> = {
  lg: {
    titleClassName: 'text-xl',
    valueClassName: 'text-5xl font-medium',
    unitClassName: 'text-xl',
  },
  md: {
    titleClassName: 'text-md',
    valueClassName: 'text-4xl font-medium',
    unitClassName: 'text-md',
  },
  sm: {
    titleClassName: '',
    valueClassName: '',
  },
};

interface ReportMetricProps {
  title?: string;
  value: ReactNode;
  unit?: UnitVariant;
  size?: MetricSize;
  className?: ClassValue;
  titleClassName?: ClassValue;
  valueClassName?: ClassValue;
  unitClassName?: ClassValue;
  leftAdornment?: React.ReactNode;
}

export default function ReportMetric({
  title,
  value,
  unit,
  size = 'sm',
  className,
  titleClassName,
  valueClassName,
  unitClassName,
  leftAdornment,
}: ReportMetricProps) {
  const {
    titleClassName: sizeTitleClassName,
    valueClassName: sizeValueClassName,
    unitClassName: sizeUnitClassName,
  } = sizeStyles[size];

  return (
    <div className={cn('space-y-2', className)}>
      <Text
        className={cn(
          'font-bold text-primary-600 font-heading',
          sizeTitleClassName,
          titleClassName
        )}
      >
        {title}
      </Text>
      <div className="flex items-baseline gap-x-2">
        {leftAdornment && leftAdornment}
        <Text
          variant="reportBody"
          className={cn('text-primary-800', sizeValueClassName, valueClassName)}
        >
          {typeof value === 'number' ? formatNumber(value) : value}
        </Text>
        <Unit
          variant={unit!}
          className={cn(sizeUnitClassName, unitClassName)}
        />
      </div>
    </div>
  );
}
